// ============================================
// Shared - Confirm Modal (ES6)
// 커스텀 confirm 모달 (alert/confirm 대체)
// ============================================

import { ModalManager } from '../modal-manager.js';

// ============================================
// Confirm Modal
// ============================================

let resolveCallback = null;

// 타입별 아이콘 SVG
const CONFIRM_ICONS = {
  danger: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
  </svg>`,
  warning: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>`,
  info: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
  </svg>`,
  confirm: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/>
  </svg>`
};

/**
 * 커스텀 confirm 모달 표시
 * @param {Object} options - 옵션
 * @param {string} options.message - 메시지 (필수)
 * @param {string} options.title - 제목 (선택, 기본: '확인')
 * @param {string} options.confirmText - 확인 버튼 텍스트 (선택, 기본: '확인')
 * @param {string} options.cancelText - 취소 버튼 텍스트 (선택, 기본: '취소')
 * @param {boolean} options.isDanger - 위험 동작 여부 (선택, 기본: false)
 * @param {string} options.warning - 경고 메시지 (선택)
 * @returns {Promise<boolean>} - 확인: true, 취소: false
 */
export function showConfirm(options) {
  const {
    message,
    title = '확인',
    confirmText = '확인',
    cancelText = '취소',
    isDanger = false,
    warning = ''
  } = typeof options === 'string' ? { message: options } : options;

  return new Promise((resolve) => {
    resolveCallback = resolve;

    // 기존 모달 제거
    const existing = document.getElementById('customConfirmModal');
    if (existing) existing.remove();

    // 아이콘 결정
    const iconType = isDanger ? 'danger' : 'confirm';
    const icon = CONFIRM_ICONS[iconType];

    // 모달 생성
    const modal = document.createElement('div');
    modal.id = 'customConfirmModal';
    modal.className = 'confirm-modal-overlay';
    modal.innerHTML = `
      <div class="confirm-modal-backdrop" data-action="cancel"></div>
      <div class="confirm-modal">
        <div class="confirm-modal-icon ${isDanger ? 'danger' : ''}">${icon}</div>
        <h3 class="confirm-modal-title">${title}</h3>
        <p class="confirm-modal-desc">
          ${message.replace(/\n/g, '<br>')}
          ${warning ? `<br><span class="confirm-modal-warning">${warning}</span>` : ''}
        </p>
        <div class="confirm-modal-buttons">
          <button class="confirm-modal-btn cancel" data-action="cancel">${cancelText}</button>
          <button class="confirm-modal-btn ${isDanger ? 'danger' : 'primary'}" data-action="confirm">${confirmText}</button>
        </div>
      </div>
    `;

    // 이벤트 핸들러
    modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'confirm') {
        closeConfirmModal(true);
      } else if (action === 'cancel') {
        closeConfirmModal(false);
      }
    });

    // 키보드 이벤트
    const handleKeydown = (e) => {
      if (e.key === 'Escape') {
        closeConfirmModal(false);
      } else if (e.key === 'Enter') {
        closeConfirmModal(true);
      }
    };
    document.addEventListener('keydown', handleKeydown);
    modal._keydownHandler = handleKeydown;

    document.body.appendChild(modal);

    // 애니메이션
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.add('open');
        ModalManager.register('customConfirmModal', () => closeConfirmModal(false), 'modal');
        ModalManager.push('customConfirmModal');
      });
    });
  });
}

function closeConfirmModal(result) {
  const modal = document.getElementById('customConfirmModal');
  if (!modal) return;

  // 키보드 이벤트 제거
  if (modal._keydownHandler) {
    document.removeEventListener('keydown', modal._keydownHandler);
  }

  modal.classList.remove('open');
  ModalManager.remove('customConfirmModal', true);
  ModalManager.unregister('customConfirmModal');

  setTimeout(() => {
    modal.remove();
    if (resolveCallback) {
      resolveCallback(result);
      resolveCallback = null;
    }
  }, 200);
}

// ============================================
// Alert Modal (단순 알림)
// ============================================

/**
 * 커스텀 alert 모달 표시
 * @param {string} message - 메시지
 * @param {string} title - 제목 (선택, 기본: '알림')
 * @returns {Promise<void>}
 */
export function showAlert(message, title = '알림') {
  return new Promise((resolve) => {
    // 기존 모달 제거
    const existing = document.getElementById('customAlertModal');
    if (existing) existing.remove();

    // 모달 생성
    const modal = document.createElement('div');
    modal.id = 'customAlertModal';
    modal.className = 'confirm-modal-overlay';
    modal.innerHTML = `
      <div class="confirm-modal-backdrop" data-action="close"></div>
      <div class="confirm-modal">
        <div class="confirm-modal-icon">${CONFIRM_ICONS.info}</div>
        <h3 class="confirm-modal-title">${title}</h3>
        <p class="confirm-modal-desc">${message.replace(/\n/g, '<br>')}</p>
        <div class="confirm-modal-buttons single">
          <button class="confirm-modal-btn primary" data-action="close">확인</button>
        </div>
      </div>
    `;

    const closeModal = () => {
      modal.classList.remove('open');
      ModalManager.remove('customAlertModal', true);
      ModalManager.unregister('customAlertModal');
      setTimeout(() => {
        modal.remove();
        resolve();
      }, 200);
    };

    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-action]')) {
        closeModal();
      }
    });

    const handleKeydown = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        document.removeEventListener('keydown', handleKeydown);
        closeModal();
      }
    };
    document.addEventListener('keydown', handleKeydown);

    document.body.appendChild(modal);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        modal.classList.add('open');
        ModalManager.register('customAlertModal', closeModal, 'modal');
        ModalManager.push('customAlertModal');
      });
    });
  });
}
